import { Image, Tag, Typography, Table, DatePicker } from "antd";
import { ApiService } from "api/api.service";
import { EventDto, EventType } from "dtos/events.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useCountdown } from "utils/hooks";
import { getNextOccurrence } from "utils/recurring.dates.util";
import { ParticipantDto } from "dtos/participant.dto";

const CountdownBox = ({ value, label }: { value: string; label: string }) => (
  <div className="flex">
    <div className="bg-[#1b1c12] text-white flex items-center justify-center px-4 py-2 text-xl font-bold">{value}</div>
    <div className="bg-[#6e50ef] text-white flex items-center justify-center px-2 py-2 text-xs font-semibold">
      {label}
    </div>
  </div>
);

const EventHero = ({ event, target }: { event: EventDto; target: Date | null }) => {
  const { days, hours, minutes, seconds } = useCountdown(target);

  return (
    <div className="w-screen h-[300px] overflow-hidden relative">
      <Image
        src={
          event.banner_image
            ? event.banner_image
            : `https://placehold.co/1600x400/EEE/AAA?text=${encodeURIComponent(event.name)}`
        }
        preview={false}
        className="w-full h-full object-cover object-center"
      />

      <div className="absolute bottom-10 left-0 lg:left-[85px] w-full flex justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-md px-6 py-3 flex flex-col items-center gap-2">
          <Typography.Text className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Time Till Next
          </Typography.Text>

          <div className="flex gap-4 items-center">
            <CountdownBox value={days} label="DAY" />
            <Typography>:</Typography>
            <CountdownBox value={hours} label="HRS" />
            <Typography>:</Typography>
            <CountdownBox value={minutes} label="MIN" />
            <Typography>:</Typography>
            <CountdownBox value={seconds} label="SEC" />
          </div>
        </div>
      </div>
    </div>
  );
};

const EventItem = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;
  const selectedTime = searchParams.get("time") || null;

  const eventDetails = ApiService.event.useGetById(id);
  const event = eventDetails.data?.result;

  const participants = ApiService.event.useGetParticipantsPaginated({
    page,
    limit,
    time: selectedTime || "",
    eventId: id!,
  });

  const participantResult = participants.data?.result;
  const participantData = participantResult?.data || [];

  if (!event) return null;

  const target = selectedTime ? new Date(selectedTime) : getNextOccurrence(event) || new Date(event.start_date);

  const currentlyRunning = () => {
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : new Date(start.getTime() + (event.duration || 0) * 1000);
    const now = new Date();
    return start <= now && now <= end;
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (p: string) => (p && p.length ? p : "-") },
    {
      title: "Registered At",
      dataIndex: "time",
      key: "time",
      render: (t: string) => dayjs(t).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "Paid",
      dataIndex: "paid",
      key: "paid",
      render: (p: boolean | undefined) => {
        if (p === undefined && event.type === EventType.FREE) return "Free";
        return p ? "Yes" : "No";
      },
    },
    {
      title: "Business Details (Type, Name, Category)",
      dataIndex: "business_details",
      key: "business_details",
      render: (bd: ParticipantDto["business_details"]) => {
        if (!bd) return "-";

        const parts = [bd.type, bd.name, bd.category].filter(Boolean);

        return parts.length ? parts.join(" · ") : "-";
      },
    },
  ];

  return (
    <>
      <EventHero event={event} target={target} />

      <DashboardLayout>
        <div className="p-8 flex flex-col items-center gap-3">
          <Typography.Title>{event.name}</Typography.Title>

          {currentlyRunning() && <Tag color="blue">ONGOING CURRENTLY</Tag>}

          <div className="flex items-center justify-center">
            {event.tags?.map((t) => (
              <Tag key={t}>{t.toUpperCase()}</Tag>
            ))}
          </div>

          <Typography.Paragraph italic className="text-wrap text-center">
            {event.description ?? ""}
          </Typography.Paragraph>
        </div>

        <div>
          <div className="flex justify-between items-center">
            <Typography.Title className="ml-3" level={4}>
              Participants
            </Typography.Title>
            <div className="flex justify-end mb-4 mr-4">
              <DatePicker
                showTime
                value={selectedTime ? dayjs(selectedTime) : null}
                placeholder="Pick any date"
                onChange={(val) => {
                  if (val) {
                    setSearchParams({
                      page: "1",
                      limit: String(limit),
                      time: val.toISOString(),
                    });
                  } else {
                    setSearchParams({
                      page: "1",
                      limit: String(limit),
                    });
                  }
                }}
              />
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={participantData}
            rowKey="_id"
            loading={participants.isPending}
            pagination={{
              current: page,
              pageSize: limit,
              total: participantResult?.totalObjects || 0,
              onChange: (newPage, newPageSize) => {
                setSearchParams({
                  page: String(newPage),
                  limit: String(newPageSize),
                  ...(selectedTime ? { time: selectedTime } : {}),
                });
              },
            }}
          />
        </div>
      </DashboardLayout>
    </>
  );
};

export default EventItem;
